import { Controller } from '@hotwired/stimulus';

import axios from "axios";

export default class extends Controller {
    static targets = [
        // meta
        'metaNumber',
        'buyConsentsTemplate',

        // participants
        'participants', 'participantsSame',
        'participantsDiff', 'participantsPayerSingle',
        'participantsPayer', 'participantsOwner',

        // debt bar
        'debtBar', 'debtTotal', 'debtItems',
        'debtContractId',

        // hero bar
        'heroName', 'heroBadge',
        'heroPulse', 'heroStatusLabel',

        // contract info
        'contractInfo',

        // payment timeline
        'timeline', 'timelineSection',

        // vacations
        'mobileVacationActionBtn', 'mobileVacationActionTitle',
        'mobileVacationActionMeta', 'mobileVacationActionSpinner',

        'vacationActionBtn', 'vacationActionLabel',
        'vacationBlock', 'vacationDate', 'vacationActionSpinner',

        // reassignment
        'reassignForm', 'reassignSubscriptionId',
    ];

    static values = {
        contracts: Array,
        addons: Object,
        today: String,
        cancelUrl: String,

        contractPayers: Array,
        isAdult: Boolean,
        selfPayerId: String,

        translations: Object,
    };

    connect() {
        this.element.addEventListener('contract-switcher:select', this.handleContractSelect.bind(this));

        this.contractModal = document.getElementById('e-contract');
        this.privacyPolicyModal = document.getElementById('privacy-policy');

        this.modalLoader = this.contractModal?.querySelector('.contract-loader') || null;
        this.modalReplacement = this.contractModal?.querySelector('.replace-content') || null;

        this.currentLanguageCode =
            (document.querySelector('meta[name="wp-language"]')?.content || 'lt').split('-')[0];

        this.bindConsentModalAccepts();

        if (this.contractsValue && this.contractsValue.length > 0) {
            this.renderContract(this.contractsValue[0]);
        }
    }

    /* ──────────────── Contract selection ──────────────── */
    handleContractSelect(event) {
        const { index } = event.detail;
        const contract = this.contractsValue[index];
        if (!contract) return;

        const switcher = this.element.querySelector('[data-controller="contract-switcher"]');
        if (switcher) {
            const ctrl = this.application.getControllerForElementAndIdentifier(switcher, 'contract-switcher');
            if (ctrl) ctrl.updateButton({ name: contract.name, id: contract.id, status: contract.status });
        }

        this.renderContract(contract);
    }

    renderContract(c) {
        this.currentContract = c;

        if (this.hasMetaNumberTarget) this.metaNumberTarget.textContent = c.id;

        if (this.hasDebtContractIdTarget) {
            this.debtContractIdTarget.value = c?.type === 'debit' ? String(c.id ?? '') : '';
        }

        if (this.hasHeroNameTarget) this.heroNameTarget.textContent = c.heroName;
        if (this.hasHeroBadgeTarget) {
            const isActive = c.status === 'active';
            this.heroBadgeTarget.className = `hero-bar__badge hero-bar__badge--${isActive ? 'active' : 'cancel'}`;
            if (this.hasHeroPulseTarget) this.heroPulseTarget.hidden = !isActive;
            if (this.hasHeroStatusLabelTarget) this.heroStatusLabelTarget.textContent = c.status_label;
        }

        if (this.hasDebtBarTarget) {
            const totalDebt = this.toNumber(c.debt);
            const items = Array.isArray(c.debt_items) ? c.debt_items : [];
            const hasDebt = totalDebt > 0;

            this.debtBarTarget.hidden = !hasDebt;

            if (hasDebt) {
                this.debtTotalTarget.textContent = `${this.fmt(totalDebt)} €`;

                this.debtItemsTarget.innerHTML = items
                    .filter((item) => this.toNumber(item.amount) > 0)
                    .map((item) => `
                        <div class="debt-bar__row">
                            <span>${this.escape(item.label ?? '')}</span>
                            <span>${this.fmt(this.toNumber(item.amount))} €</span>
                        </div>
                    `)
                    .join('');
            }
        }

        if (this.hasVacationBlockTarget) {
            const hasVacation = !!c.vacation_until;
            this.vacationBlockTarget.hidden = !hasVacation;
            if (hasVacation && this.hasVacationDateTarget) {
                this.vacationDateTarget.textContent = c.vacation_from + " - " + c.vacation_until;
            }
        }

        this.updateVacationAction(c);
        this.updateTimeline(c);
        this.updateContractInfo(c);
        this.updateParticipants(c);
        this.updateDebtCheckoutModal(c);
    }

    openVacationModal() {
        const c = this.currentContract;
        if (!c || !c.vacation_modal_id) return;

        document.dispatchEvent(new CustomEvent('modal:open', {
            detail: { id: c.vacation_modal_id }
        }));
    }

    updateTimeline(c) {
        const items = (c?.type !== 'cash' && Array.isArray(c?.timeline?.items))
            ? c.timeline.items
            : [];

        const hasTimelineData = items.length > 0;

        if (this.hasTimelineSectionTarget) {
            this.timelineSectionTarget.hidden = !hasTimelineData;
        }

        if (!this.hasTimelineTarget) return;

        // Can't render a broken/partial timeline — clear it and bail.
        if (!hasTimelineData) {
            this.timelineTarget.innerHTML = '';
            return;
        }

        const perMonth = this.t('per_month_short_slash', '/mėn.');

        const itemsHtml = items.map((item, i) => {
            const isLast = i === items.length - 1;
            const dim = !!item?.dim;
            const key = this.escapeAttr(item?.key ?? '');

            return `
                <div class="payment-timeline__item">
                    <div class="payment-timeline__line">
                        <div class="payment-timeline__dot ${dim ? 'payment-timeline__dot--dim' : ''}"></div>
                        ${!isLast ? '<div class="payment-timeline__connector"></div>' : ''}
                    </div>
                    <div class="payment-timeline__content">
                        <div>
                            <div class="payment-timeline__label ${dim ? 'payment-timeline__label--dim' : ''}" data-timeline-key="${key}-label">${this.escape(item?.label ?? '')}</div>
                            <div class="payment-timeline__date" data-timeline-key="${key}-date">${this.escape(item?.date ?? '')}</div>
                            ${item?.sub ? `<div class="payment-timeline__date" data-timeline-key="${key}-sub">${this.escape(item.sub)}</div>` : ''}
                        </div>
                        <div class="payment-timeline__price-wrap">
                            <div class="payment-timeline__price ${dim ? 'payment-timeline__price--dim' : ''}" data-timeline-key="${key}-price">${this.escape(item?.price ?? '')}</div>
                            <div class="payment-timeline__per">${perMonth}</div>
                        </div>
                    </div>
                </div>
            `;
        }).join('');

        const progress = c.timeline.progress;
        const progressHtml = progress ? `
            <div class="payment-timeline__progress">
                <div class="payment-timeline__progress-head">
                    <span class="payment-timeline__progress-label">${this.escape(progress.label ?? this.t('progress_label', 'Sutarties eiga'))}</span>
                    <span data-timeline-key="progress-range">${this.escape(progress.range ?? '')}</span>
                </div>
                <div class="payment-timeline__progress-track">
                    <div class="payment-timeline__progress-fill" style="width: ${Number(progress.percent) || 0}%" data-timeline-key="progress-fill"></div>
                </div>
            </div>
        ` : '';

        this.timelineTarget.innerHTML = itemsHtml + progressHtml;
    }

    getModalDebtTotal(c) {
        const items = Array.isArray(c?.debt_items) ? c.debt_items : [];
        if (items.length > 0) {
            return items.reduce((sum, item) => sum + this.toNumber(item?.amount), 0);
        }
        return this.toNumber(c?.debt);
    }

    updateDebtCheckoutModal(c) {
        const modal = document.getElementById('checkout-debt-modal');
        if (!modal) return;

        const contractInput = modal.querySelector('[data-debt-modal-contract-id]');
        const amountEl = modal.querySelector('[data-debt-modal-amount]');
        const discountEl = modal.querySelector('[data-debt-modal-discount]');
        const couponInput = modal.querySelector('input[name="code"]');
        const appliedCouponInput = modal.querySelector('input[name="applied_coupon_code"]');

        const isDebit = c?.type === 'debit';
        const contractId = isDebit ? String(c?.id ?? '') : '';
        const debtTotal = isDebit ? this.getModalDebtTotal(c) : 0;

        if (contractInput) contractInput.value = contractId;
        if (amountEl) amountEl.textContent = `${this.fmt(debtTotal)} €`;

        if (discountEl) discountEl.textContent = '';
        if (couponInput) couponInput.value = '';
        if (appliedCouponInput) appliedCouponInput.value = '';
    }

    updateContractInfo(c) {
        if (!this.hasContractInfoTarget) return;

        const list = this.contractInfoTarget;
        const isCash = c.type === 'cash';

        const set = (key, value) => {
            const row = list.querySelector(`[data-contract-info-key="${key}"]`);
            if (!row) return;
            const v = row.querySelector('[data-contract-info-value]');
            if (v) v.textContent = value ?? '';
        };

        const setLabel = (key, text) => {
            const row = list.querySelector(`[data-contract-info-key="${key}"]`);
            if (!row) return;
            const l = row.querySelector('[data-contract-info-label]');
            if (l) l.textContent = text ?? '';
        };

        const toggle = (key, hidden) => {
            const row = list.querySelector(`[data-contract-info-key="${key}"]`);
            if (row) row.hidden = !!hidden;
        };

        const items = Array.isArray(c?.timeline?.items) ? c.timeline.items : [];
        const discountItem = items.find((i) => i?.key === 'discount') || null;
        const stdItem = items.find((i) => i?.key === 'std') || null;

        const hasDiscountPeriod = !!discountItem && !!stdItem;

        const flatFee = hasDiscountPeriod
            ? this.toNumber(discountItem?.price)
            : this.toNumber(stdItem?.price);

        const regularFee = hasDiscountPeriod
            ? this.toNumber(stdItem?.price)
            : null;

        set('valid-from', c.valid_from);
        set('valid-until', c.valid_to);
        set('holidays', c.vacation_days_left);
        set('termination-date', c.terminationDate);

        setLabel('valid-from', isCash ? this.t('valid_from_cash', 'Galioja nuo:') : this.t('valid_from_contract', 'Įsipareigojimas nuo:'));
        setLabel('valid-until', isCash ? this.t('valid_until_cash', 'Galioja iki:') : this.t('valid_until_contract', 'Įsipareigojimas iki:'));

        set('flat-fee', !isCash ? this.fmt(flatFee) : '0');
        set('regular-price', !isCash && regularFee !== null ? this.fmt(regularFee) : '');
        set('debt', !isCash ? this.fmt(this.toNumber(c.debt)) : '0');

        if (!isCash) {
            setLabel(
                'flat-fee',
                hasDiscountPeriod
                    ? this.t('flat_fee_first_months', 'Pirmus mėnesius mokama:')
                    : this.t('flat_fee_regular', 'Mokama:')
            );
        }

        toggle('flat-fee', isCash);
        toggle('regular-price', isCash || !hasDiscountPeriod);
        toggle('termination-date', isCash || !c.terminationDate);
        toggle('debt', isCash || this.toNumber(c.debt) <= 0);
    }

    updateParticipants(c) {
        const payer = String(c?.payer ?? '');
        const owner = String(c?.owner ?? '');
        const same = payer === owner;

        this.participantsTargets.forEach((root) => {
            const sameBlock = root.querySelector('[data-membership-target~="participantsSame"]');
            const diffBlock = root.querySelector('[data-membership-target~="participantsDiff"]');

            if (sameBlock) sameBlock.hidden = !same;
            if (diffBlock) diffBlock.hidden = same;

            root.querySelectorAll('[data-membership-target~="participantsPayerSingle"]')
                .forEach((el) => { el.textContent = payer; });

            root.querySelectorAll('[data-membership-target~="participantsPayer"]')
                .forEach((el) => { el.textContent = payer; });

            root.querySelectorAll('[data-membership-target~="participantsOwner"]')
                .forEach((el) => { el.textContent = owner; });
        });
    }

    openBuyPanel(event) {
        const id = event.currentTarget.dataset.addonId;
        const price = parseFloat(event.currentTarget.dataset.addonPrice || "0");

        const info = (this.addonsValue && this.addonsValue[id]) || {};
        const name = info.name || "";

        const today = this.todayValue ? new Date(this.todayValue) : new Date();
        const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
        const remainingDays = daysInMonth - today.getDate() + 1;

        const dailyRate = price / daysInMonth;
        const remainder = +(dailyRate * remainingDays).toFixed(2);
        const total = +(remainder + price).toFixed(2);

        const payerSection = this.buildPayerSection();

        const consentsHtml = this.getBuyConsentsHtml();

        const html = `
      <div class="panel-hero">
        <span class="panel-hero__name">${this.escape(name)}</span>
        <span class="panel-hero__price">+${this.escape(String(price))} ${this.t('panel_per_month_short', '€/mėn.')}</span>
      </div>
      <div class="panel-section">
        <div class="panel-section__title">${this.t('panel_about_service', 'Apie paslaugą')}</div>
        <div class="panel-section__desc">${this.escape(info.service_description || '')}</div>
        ${info.benefits ? `
          <div style="margin-top:12px;padding-top:12px;border-top:1px solid var(--color-border);">
            <div class="panel-section__title">${this.t('panel_benefits', 'Nauda')}</div>
            <div class="panel-section__desc">${this.escape(info.benefits)}</div>
          </div>` : ''}
      </div>
      
      <form class="panel-form" data-controller="axios-form" method="post" action="/wp-json/api/dashboard/pay" novalidate>
      
      ${payerSection}
      
      <div class="panel-section">
          <div class="panel-section__title">${this.t('panel_invoice', 'EL. SĄSKAITA')}</div>
        
          <div role="radiogroup" aria-label="${this.escapeAttr(this.t('panel_choice_aria', 'Pasirinkimas'))}">
            <button type="button"
                    class="panel-option panel-option--selected"
                    role="radio"
                    aria-checked="true"
                    data-action="click->slide-panel#selectOption"
                    data-radio-group="invoice_choice"
                    data-radio-value="0"
                    data-radio-toggle-target="[data-iban-wrap]"
                    data-radio-toggle-show="0"
                    data-radio-input="invoice_choice">
              <div class="panel-option__radio"><div class="panel-option__radio-dot"></div></div>
              <div>
                <div class="panel-option__label">${this.t('panel_invoice_no_bank', 'NENORIU GAUTI SĄSKAITŲ Į BANKĄ')}</div>
              </div>
            </button>
        
            <button type="button"
                    class="panel-option"
                    role="radio"
                    aria-checked="false"
                    data-action="click->slide-panel#selectOption"
                    data-radio-group="invoice_choice"
                    data-radio-value="1"
                    data-radio-toggle-target="[data-iban-wrap]"
                    data-radio-toggle-show="1"
                    data-radio-input="invoice_choice">
              <div class="panel-option__radio"><div class="panel-option__radio-dot"></div></div>
              <div>
                <div class="panel-option__label">${this.t('panel_invoice_yes_bank', 'NORIU GAUTI SĄSKAITAS Į BANKĄ')}</div>
              </div>
            </button>
          </div>
        
          <input type="hidden" name="invoice_choice" value="0">
          
          
          <div class="panel-input mt-1" data-iban-wrap hidden>
                <label class="panel-input__label" for="panel-iban">${this.t('panel_iban_label', 'Banko sąskaitos numeris (IBAN)')}</label>
              <input id="panel-iban"
                    class="panel-input__control"
                    type="text"
                    name="iban"
                    placeholder="LT00 0000 0000 0000 0000"
                    data-action="input->slide-panel#formatIban">
          </div>
        </div>
       
      <div class="panel-section">
        <div class="panel-section__title">${this.t('panel_payment', 'Mokėjimas')}</div>
        <div class="panel-section__row">
          <span class="panel-section__row-label">${this.t('panel_monthly_fee', 'Mėnesinė įmoka')}</span>
          <span class="panel-section__row-value panel-section__row-value--b">+${this.fmt(price)} ${this.t('panel_per_month_short', '€/mėn.')}</span>
        </div>
        <div class="panel-section__row">
          <span class="panel-section__row-label">${this.tf('panel_remaining_days', 'Šio mėn. likutis ({days} d.)', { days: remainingDays })}</span>
          <span class="panel-section__row-value panel-section__row-value--b">${this.fmt(remainder)} €</span>
        </div>
        <div class="panel-section__row">
          <span class="panel-section__row-label">${this.t('panel_first_full_month', 'Pirmas pilnas mėnuo')}</span>
          <span class="panel-section__row-value panel-section__row-value--b">${this.fmt(price)} €</span>
        </div>
        <div class="panel-section__row panel-section__row--big">
          <span class="panel-section__row-label">${this.t('panel_pay_now', 'Mokėti dabar')}</span>
          <span class="panel-section__row-value">${this.fmt(total)} €</span>
        </div>
      </div>
      <div class="panel-note">${this.t('panel_unlimited_notice', 'Paslauga neterminuota – nutraukite bet kada.')}</div>
      <div class="panel-agree"
           data-action="click->slide-panel#toggleAgree"
           data-agree-for="buy">
        <div class="panel-agree__check">
          <svg class="panel-agree__icon" width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M2 5l2.5 2.5L8 3" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="panel-agree__text">${this.t('panel_agree_activate_today', 'Sutinku su paslaugų sąlygomis ir patvirtinu aktyvavimą nuo šiandien.')}</div>
      </div>
      
      ${consentsHtml}
      
      <button type="submit"
              class="panel-confirm"
              data-agree-id="buy"
              data-confirm-name="${this.escape(name)}"
              disabled>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7l3 3 6-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        
        ${this.t('panel_confirm', 'Patvirtinti')}
      </button>
      
      <input type="hidden" name="cancel_url" value="${this.escape(this.cancelUrlValue)}">
      <input type="hidden" name="item_id" value="${this.escape(String(id))}">
      <input type="hidden" name="item_type" value="addon">
        </form>`;

        this.panelController.open({ title: this.t('panel_add_service', 'Pridėti paslaugą'), body: html });
        requestAnimationFrame(() => this.bootstrapBuyPanelConsents());
    }

    openCancelPanel(event) {
        const ds = event.currentTarget.dataset;

        const contractID = ds.addonContractId;
        const name = ds.addonName;
        const price = ds.addonPrice;

        const hasTermination =
            ds.addonHasTermination === '1' ||
            ds.addonHasTermination === 'true';

        // intentional return, no functionality to cancel termination as of now
        if (hasTermination) {
            return;
        }

        const terminationDate = ds.addonTerminationDate || '';

        const today = this.todayValue ? new Date(this.todayValue) : new Date();
        const earliest = new Date(today.getFullYear(), today.getMonth() + 2, 1);
        const earliestStr = this.formatDate(earliest);
        const earliestLabel = this.formatDateLabel(earliest);
        const prevMonth = this.prevMonthLabel(earliest);

        const actionUrl = hasTermination
            ? '/wp-json/api/dashboard/cancel-addon-termination'
            : '/wp-json/api/dashboard/terminate-addon';

        const panelTitle = hasTermination
            ? this.t('panel_cancel_title_undo', 'Atšaukti paslaugos nutraukimą')
            : this.t('panel_cancel_title_terminate', 'Nutraukti paslaugą');

        const confirmText = hasTermination
            ? this.t('panel_cancel_confirm_undo', 'Patvirtinti atšaukimą')
            : this.t('panel_cancel_confirm_terminate', 'Patvirtinti nutraukimą');

        const agreeText = hasTermination
            ? this.t('panel_cancel_agree_undo', 'Patvirtinu, kad noriu atšaukti šios paslaugos nutraukimą.')
            : this.t('panel_cancel_agree_terminate', 'Patvirtinu, kad noriu nutraukti šią paslaugą nuo pasirinktos datos.');

        const bodyTop = hasTermination
            ? `
        <div class="panel-section">
          <div class="panel-section__title">${this.t('panel_cancel_scheduled', 'Nutraukimas suplanuotas')}</div>
          <div style="font-size:13px;color:var(--color-text-2);margin-bottom:12px;line-height:1.6;">
            ${this.t('panel_cancel_scheduled_prefix', 'Paslauga šiuo metu suplanuota nutraukti nuo')} <strong>${this.escape(terminationDate)}</strong>.
            ${this.t('panel_cancel_scheduled_suffix', 'Galite atšaukti nutraukimą.')}
          </div>
        </div>
      `
            : `
        <div class="panel-section">
          <div class="panel-section__title">${this.t('panel_cancel_pick_date', 'Pasirinkite nutraukimo datą')}</div>
          <div style="font-size:13px;color:var(--color-text-2);margin-bottom:12px;line-height:1.6;">
            ${this.tf('panel_cancel_only_first_day', 'Paslauga nutraukiama tik nuo mėnesio 1 dienos. Anksčiausia galima data — {date}.', { date: `<strong>${this.escape(earliestStr)}</strong>` })}
          </div>
          <div class="panel-option panel-option--selected">
            <div class="panel-option__radio"><div class="panel-option__radio-dot"></div></div>
            <div>
              <div class="panel-option__label">${this.tf('panel_cancel_from', 'Nuo {date}', { date: this.escape(earliestLabel) })}</div>
              <div class="panel-option__note">${this.tf('panel_cancel_last_payment', 'Paskutinis mok. — {month}', { month: this.escape(prevMonth) })}</div>
            </div>
          </div>
          <div class="panel-note">${this.t('panel_cancel_after_notice', 'Po nutraukimo datos paslauga nebebus taikoma.')}</div>
        </div>
      `;

        const html = `
      <div class="panel-hero">
        <span class="panel-hero__name">${this.escape(name)}</span>
        <span class="panel-hero__price">${this.escape(price)} ${this.t('panel_per_month_short', '€/mėn.')}</span>
      </div>

      ${bodyTop}

      <div class="panel-agree"
           data-action="click->slide-panel#toggleAgree"
           data-agree-for="cancel">
        <div class="panel-agree__check">
          <svg class="panel-agree__icon" width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M2 5l2.5 2.5L8 3" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="panel-agree__text">${agreeText}</div>
      </div>

      <form class="panel-form" data-controller="axios-form" method="post" action="${actionUrl}">      
        <button type="submit"
                class="panel-confirm"
                data-agree-id="cancel"
                data-confirm-name="${this.escape(name)}"
                disabled>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M2.5 7l3 3 6-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          ${confirmText}
        </button>

        <input type="hidden" name="contract_id" value="${this.escape(String(contractID))}">
      </form>`;

        this.panelController.open({ title: panelTitle, body: html });
    }

    updateVacationAction(c) {
        const hasPause = !!c?.vacation_until;
        const canCancel = !!c?.vacation_can_cancel;

        if (this.hasVacationActionBtnTarget && this.hasVacationActionLabelTarget) {
            if (canCancel) {
                this.vacationActionBtnTarget.hidden = false;
                this.vacationActionLabelTarget.textContent = this.t('vacation_stop', 'Stabdyti atostogas');
                this.vacationActionBtnTarget.dataset.vacationMode = 'cancel';
            } else if (hasPause) {
                this.vacationActionBtnTarget.hidden = true;
                this.vacationActionLabelTarget.textContent = '';
                this.vacationActionBtnTarget.dataset.vacationMode = '';
            } else {
                this.vacationActionBtnTarget.hidden = false;
                this.vacationActionLabelTarget.textContent = this.t('vacation_activate', 'Aktyvuoti atostogas');
                this.vacationActionBtnTarget.dataset.vacationMode = 'activate';
            }
        }

        if (this.hasMobileVacationActionBtnTarget && this.hasMobileVacationActionTitleTarget) {
            if (canCancel) {
                this.mobileVacationActionBtnTarget.hidden = false;
                this.mobileVacationActionBtnTarget.dataset.vacationMode = 'cancel';
                this.mobileVacationActionTitleTarget.textContent = this.t('vacation_stop', 'Stabdyti atostogas');
                if (this.hasMobileVacationActionMetaTarget) {
                    this.mobileVacationActionMetaTarget.textContent = c.vacation_until
                        ? this.tf('vacation_until', 'Iki {date}', { date: c.vacation_until })
                        : '';
                }
            } else if (hasPause) {
                this.mobileVacationActionBtnTarget.hidden = true;
                this.mobileVacationActionBtnTarget.dataset.vacationMode = '';
            } else {
                this.mobileVacationActionBtnTarget.hidden = false;
                this.mobileVacationActionBtnTarget.dataset.vacationMode = 'activate';
                this.mobileVacationActionTitleTarget.textContent = this.t('vacation_activate', 'Aktyvuoti atostogas');
                if (this.hasMobileVacationActionMetaTarget) {
                    const left = Number.isFinite(Number(c.pause_days_left)) ? Number(c.pause_days_left) : null;
                    this.mobileVacationActionMetaTarget.textContent = left !== null
                        ? this.tf('vacation_days_left', '{days} d. liko', { days: left })
                        : '';
                }
            }
        }
    }

    handleVacationAction(event) {
        const c = this.currentContract;
        if (!c) return;

        const btn = event?.currentTarget || null;
        const mode =
            btn?.dataset?.vacationMode ||
            this.vacationActionBtnTarget?.dataset?.vacationMode ||
            this.mobileVacationActionBtnTarget?.dataset?.vacationMode;

        if (mode === 'cancel') {
            if (btn) this.setVacationButtonLoading(btn, true);

            if (c.vacation_cancel_form) {
                const form = document.getElementById(c.vacation_cancel_form);
                if (form) form.requestSubmit();
            }
            return;
        }

        if (mode === 'activate' && c.vacation_modal_id) {
            document.dispatchEvent(new CustomEvent('modal:open', {
                detail: { id: c.vacation_modal_id }
            }));
        }
    }

    setVacationButtonLoading(button, isLoading) {
        if (!button) return;
        button.disabled = isLoading;
        button.classList.toggle('is-loading', isLoading);

        const spinner = button.querySelector(
            '[data-membership-target="vacationActionSpinner"], [data-membership-target="mobileVacationActionSpinner"]'
        );
        if (spinner) spinner.hidden = !isLoading;
    }

    getCurrentSubscriptionId(contract = this.currentContract) {
        if (!contract) return '';

        return String(
            contract.subscription_id ??
            contract.contract?.id ??
            contract.id ??
            ''
        );
    }

    reassignMembership(event) {
        event?.preventDefault();

        if (!this.hasReassignFormTarget || !this.hasReassignSubscriptionIdTarget) return;

        const subscriptionId = this.getCurrentSubscriptionId();
        if (!subscriptionId) return;

        this.reassignSubscriptionIdTarget.value = subscriptionId;
        this.reassignFormTarget.requestSubmit();
    }

    buildPayerSection() {
        if (this.isAdultValue) {
            return `<input type="hidden" name="payer_nsoft_id" value="${this.escapeAttr(String(this.selfPayerIdValue || ''))}">`;
        }

        const payers = Array.isArray(this.contractPayersValue)
            ? this.contractPayersValue.filter((p) => p && p.id)
            : [];

        if (payers.length === 0) {
            return `
          <div class="panel-section">
            <div class="panel-section__title">${this.t('payer', 'Mokėtojas')}</div>
            <div class="panel-note">${this.t('payer_not_found', 'Nepavyko rasti galimo mokėtojo. Susisiekite su administracija.')}</div>
          </div>
          <input type="hidden" name="payer_nsoft_id" value="">
        `;
        }

        const firstId = String(payers[0].id);

        const options = payers.map((payer, index) => {
            const id = String(payer.id);
            const name = String(payer.name || '');
            const selected = index === 0;

            return `
          <button type="button"
                  class="panel-option ${selected ? 'panel-option--selected' : ''}"
                  role="radio"
                  aria-checked="${selected ? 'true' : 'false'}"
                  data-action="click->slide-panel#selectOption"
                  data-radio-group="payer_nsoft_id"
                  data-radio-value="${this.escapeAttr(id)}"
                  data-radio-input="payer_nsoft_id">
            <div class="panel-option__radio"><div class="panel-option__radio-dot"></div></div>
            <div>
              <div class="panel-option__label">${this.escape(name)}</div>
            </div>
          </button>
        `;
        }).join('');

        return `
      <div class="panel-section">
        <div class="panel-section__title">${this.t('payer', 'Mokėtojas')}</div>
        <div role="radiogroup" aria-label="${this.escapeAttr(this.t('payer_choice_aria', 'Mokėtojo pasirinkimas'))}">
          ${options}
        </div>
        <input type="hidden" name="payer_nsoft_id" value="${this.escapeAttr(firstId)}">
      </div>
    `;
    }

    get panelController() {
        const el = document.querySelector('[data-controller~="slide-panel"]');
        return el ? this.application.getControllerForElementAndIdentifier(el, 'slide-panel') : null;
    }

    getBuyConsentsHtml() {
        if (!this.hasBuyConsentsTemplateTarget) return '';
        return this.buyConsentsTemplateTarget.innerHTML.trim();
    }

    t(key, fallback = '') {
        return this.translationsValue?.[key] ?? fallback;
    }

    tf(key, fallback = '', vars = {}) {
        let s = this.t(key, fallback);
        Object.entries(vars).forEach(([k, v]) => {
            s = s.replaceAll(`{${k}}`, String(v ?? ''));
        });
        return s;
    }

    monthName(index) {
        const fallbackMonths = [
            'Sausio', 'Vasario', 'Kovo', 'Balandžio', 'Gegužės', 'Birželio',
            'Liepos', 'Rugpjūčio', 'Rugsėjo', 'Spalio', 'Lapkričio', 'Gruodžio',
        ];
        return this.t(`month_${index + 1}`, fallbackMonths[index] ?? '');
    }

    fmt(n) {
        return Number(n).toFixed(2).replace('.', ',');
    }

    formatDate(d) {
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${d.getFullYear()}-${m}-${day}`;
    }

    formatDateLabel(d) {
        return `1 ${this.monthName(d.getMonth())} ${d.getFullYear()}`;
    }

    prevMonthLabel(d) {
        const p = new Date(d.getFullYear(), d.getMonth() - 1, 1);
        return `${this.monthName(p.getMonth())} ${p.getFullYear()}`;
    }

    escape(s) {
        return String(s).replace(/[&<>"']/g, (c) => ({
            '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
        }[c]));
    }

    toNumber(v) {
        if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
        const n = Number(String(v ?? '').replace(',', '.').replace(/[^\d.-]/g, ''));
        return Number.isFinite(n) ? n : 0;
    }

    escapeAttr(value) {
        return String(value).replace(/"/g, '\\"');
    }

    // ------------------------------ CONSENTS ----------------------------------------
// ------------------------------ CONSENTS ----------------------------------------
    bootstrapBuyPanelConsents() {
        const form = this.getActiveBuyForm();
        if (!form) return;

        // e_contract is rendered disabled in consent partial; enable it for panel flow
        const eContractInput = form.querySelector('input[name="consents[e_contract]"]');
        if (eContractInput) {
            eContractInput.disabled = false;
            eContractInput.closest('label')?.classList.remove('disabled');
        }

        // keep privacy consent interactive too (if present)
        const privacyInput = form.querySelector('input[name="consents[personal_data]"]');
        if (privacyInput) {
            privacyInput.disabled = false;
            privacyInput.closest('label')?.classList.remove('disabled');
        }

        this.bindBuyPanelConsentLabels();
    }

    async showEContractAcceptModal(event, force = false) {
        const form = this.getActiveBuyForm();
        if (!form || !this.contractModal) return;

        event?.preventDefault();
        event?.stopPropagation();

        const acceptContractInput = form.querySelector('input[name="consents[e_contract]"]');
        if (!acceptContractInput && !force) return;

        if (acceptContractInput) {
            acceptContractInput.disabled = false;
            acceptContractInput.closest('label')?.classList.remove('disabled');
        }

        const modalController = this.application.getControllerForElementAndIdentifier(this.contractModal, 'modal');
        if (!modalController?.showModal) return;

        if (this.modalLoader) this.modalLoader.classList.remove('d-none');
        if (this.modalReplacement) this.modalReplacement.innerHTML = '';

        modalController.showModal();

        const formData = new FormData(form);

        const addonId = formData.get('item_id');
        if (addonId) {
            formData.set('item_id', String(addonId));
        }

        const queryString = new URLSearchParams(formData).toString();
        const urlDebit = `/wp-json/api/dashboard/get-contract-document?rest_language=${encodeURIComponent(this.currentLanguageCode)}&${queryString}`;

        let response;
        try {
            const res = await axios.get(urlDebit);
            response = res?.data?.data;
        } catch (error) {
            response = error?.response?.data?.data;
        }

        if (this.modalLoader) this.modalLoader.classList.add('d-none');

        if (response?.contract && this.modalReplacement) {
            this.modalReplacement.innerHTML = '<iframe></iframe>';

            setTimeout(() => {
                const iframe = this.modalReplacement.firstElementChild;
                if (!iframe?.contentWindow?.document?.body) return;
                iframe.contentWindow.document.body.innerHTML = response.contract;
                iframe.height = `${iframe.contentWindow.document.body.scrollHeight}px`;
            }, 0);
        } else if (this.modalReplacement) {
            const messageNode = document.createElement('span');
            messageNode.classList.add('general-error');
            messageNode.innerHTML =
                document.querySelector('meta[name="global_axios_validator_error"]')?.content || 'Error';
            this.modalReplacement.innerHTML = '';
            this.modalReplacement.appendChild(messageNode);
        }
    }

    bindBuyPanelConsentLabels() {
        const panelEl = this.panelController?.element;
        if (!panelEl) return;

        if (this._panelConsentClickHandler) {
            panelEl.removeEventListener('click', this._panelConsentClickHandler);
        }

        this._panelConsentClickHandler = (event) => {
            const contractLabel = event.target.closest('.consent__e_contract .label-text');
            if (contractLabel) {
                this.showEContractAcceptModal(event, true);
                return;
            }

            const privacyLabel = event.target.closest('.consent__personal_data .label-text');
            if (privacyLabel) {
                event.preventDefault();
                event.stopPropagation();
                this.showConsentModal(this.privacyPolicyModal);
            }
        };

        panelEl.addEventListener('click', this._panelConsentClickHandler);
    }

    bindConsentModalAccepts() {
        if (this.contractModal) {
            const acceptBtn = this.contractModal.querySelector('.modal-accept');
            if (acceptBtn && !acceptBtn.dataset.bound) {
                acceptBtn.dataset.bound = '1';
                acceptBtn.addEventListener('click', () => {
                    this.setPanelConsentChecked('consents[e_contract]');
                    this.hideModal(this.contractModal);
                });
            }
        }

        if (this.privacyPolicyModal) {
            const acceptBtn = this.privacyPolicyModal.querySelector('.modal-accept');
            if (acceptBtn && !acceptBtn.dataset.bound) {
                acceptBtn.dataset.bound = '1';
                acceptBtn.addEventListener('click', () => {
                    this.setPanelConsentChecked('consents[personal_data]');
                    this.hideModal(this.privacyPolicyModal);
                });
            }
        }
    }

    setPanelConsentChecked(inputName) {
        const form = this.getActiveBuyForm();
        if (!form) return;

        const selectorSafeName = inputName.replace(/"/g, '\\"');
        const input = form.querySelector(`input[name="${selectorSafeName}"]`);
        if (!input) return;

        input.disabled = false;
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    showConsentModal(modalEl) {
        if (!modalEl) return;

        const ctrl = this.application.getControllerForElementAndIdentifier(modalEl, 'modal');
        if (ctrl?.showModal) {
            ctrl.showModal();
        } else {
            document.dispatchEvent(new CustomEvent('modal:open', { detail: { id: modalEl.id } }));
        }
    }

    getActiveBuyForm() {
        return this.panelController?.element?.querySelector('.panel-form') || null;
    }

    hideModal(modalEl) {
        if (!modalEl) return;

        const ctrl = this.application.getControllerForElementAndIdentifier(modalEl, 'modal');
        if (ctrl?.hideModal) {
            ctrl.hideModal();
        } else {
            document.dispatchEvent(new CustomEvent('modal:close', { detail: { id: modalEl.id } }));
        }
    }

    disconnect() {
        const panelEl = this.panelController?.element;
        if (panelEl && this._panelConsentClickHandler) {
            panelEl.removeEventListener('click', this._panelConsentClickHandler);
        }
    }
}
